import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@nimbus-ds/components';
import { NotificationIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Country } from '@tiendanube/common/src/enums';
import { CATALOG_CHAT_AGENT_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { trackingMenuPrimaryAccountClick } from 'App/tracking';
import { AgentChatCTA, BackNavigation } from 'commons/components';
import { openWindow } from 'commons/utils/window';
import {
  useGetIsUserFullRole,
  useGetStoreInfo,
  useHeaderMenuData,
  useIsLogged,
} from 'domains/Auth/hooks';
import { ProductUpdatesLink, ProfileMenu } from './components';
import useTopBar from './useTopBar';
import ButtonBackPreviousVersion from '../Migrations/ButtonBackPreviousVersion';
import { SupportChanels } from '../SupportChanels';
import './Topbar.scss';

function TopBar(): JSX.Element {
  const { t } = useTranslation('common');
  const { headerNavigation, isShowMenu } = useTopBar();
  const { name } = useHeaderMenuData();
  const { isLogin } = useIsLogged();
  const [showPopover, setShowPopover] = useState(false);
  const location = useLocation();
  const isAdmin = useGetIsUserFullRole();
  const [hasAgentTag] = useHasTags(CATALOG_CHAT_AGENT_ENABLED);
  const { country } = useGetStoreInfo();

  const handleAccountClick = () => {
    if (!showPopover) {
      trackingMenuPrimaryAccountClick();
    }
    return setShowPopover(!showPopover);
  };

  const handleClickNews = () => {
    openWindow(t('topbar.whatsNewLink'), true);
  };

  useEffect(() => {
    setShowPopover(false);
  }, [location]);

  const handleShowPopover = (status: boolean) => {
    setShowPopover(status);
  };

  const shouldRenderAgentChatCTA = useMemo(
    () => hasAgentTag && isAdmin,
    [hasAgentTag, isAdmin],
  );

  const isBrOrIsMx = country === Country.MX || country === Country.BR;

  return (
    <div className="stratus--top-bar">
      <Box display="flex" justifyContent="space-between">
        <Box>
          {headerNavigation && (
            <BackNavigation onClick={headerNavigation.onClick} from="topbar">
              {headerNavigation.children}
            </BackNavigation>
          )}
        </Box>
        {isShowMenu && (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={{ xs: '1', md: '2' }}
          >
            {shouldRenderAgentChatCTA && <AgentChatCTA />}
            {!shouldRenderAgentChatCTA && isBrOrIsMx && <ProductUpdatesLink />}
            <Tooltip content={t('topbar.whatsNew')} arrow={false}>
              <IconButton
                size="2.125rem"
                source={<NotificationIcon size={18} />}
                backgroundColor="transparent"
                borderColor={{
                  xs: 'transparent',
                  hover: 'neutral-interactiveHover',
                }}
                data-style="icon-button-override"
                onClick={handleClickNews}
              />
            </Tooltip>
            {isLogin && <SupportChanels />}
            <ProfileMenu
              name={name}
              showPopover={showPopover}
              onShowPopover={handleShowPopover}
              onAccountClick={handleAccountClick}
            />
            <ButtonBackPreviousVersion />
          </Box>
        )}
      </Box>
    </div>
  );
}

export default TopBar;
